import { Loading } from "src/components/common";
import { useState, useEffect } from "react";
import { FilePenLine, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "src/shadcn/ui/button";
import { Textarea } from "src/shadcn/ui/textarea";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/shadcn/ui/tab";
import {
  IAssessmentReport,
  IJob,
  IResume,
  ISuggestionResumeFile,
} from "src/models";
import {
  updateSkillExperience,
  getResumesSuggestions,
  generateResumeSuggestions,
} from "src/services/job";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/shadcn/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/shadcn/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/shadcn/ui/tooltip";

export interface IRevisedResumeReport {
  report: IAssessmentReport;
  resume: IResume;
  summary: string;
}

interface Props {
  resumeId: string | null;
  currentJob: IJob;
  currentReport: IRevisedResumeReport | null;
  onActiveStep?: () => void;
  onReportCallBack?: () => void;
}

const EditSkillExperienceInput = ({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
}) => {
  return (
    <Textarea
      rows={1}
      value={value}
      className="min-h-[40px]"
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  );
};

const RevisedResume: React.FC<Props> = ({
  currentJob,
  resumeId,
  currentReport,
  onReportCallBack,
}) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const tab = searchParams.get("tab");
  const currentStep = searchParams.get("step");

  // states
  const [report, setReport] = useState<IRevisedResumeReport | undefined>(
    undefined,
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isReAssessLoading, setIsReAssessLoading] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<
    "add_skills_experience" | "revised_resume"
  >("add_skills_experience");
  const [suggestionResume, setSuggestionResume] =
    useState<ISuggestionResumeFile | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // functions
  const handleChangeContent = (id: string, content: string, type: string) => {
    setReport((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedReport = { ...prev };

      if (type === "desire_to_have") {
        const newDesireToHave = updatedReport?.report?.desire_to_have?.map(
          (item) => {
            if (item.id === id) {
              return { ...item, content };
            }
            return item;
          },
        );

        if (newDesireToHave) {
          return {
            ...updatedReport,
            report: {
              ...updatedReport.report,
              desire_to_have: newDesireToHave,
            },
          };
        }

        return updatedReport;
      }

      if (type === "must_have") {
        const newMustHave = updatedReport?.report?.must_have?.map((item) => {
          if (item.id === id) {
            return { ...item, content };
          }
          return item;
        });

        if (newMustHave) {
          return {
            ...updatedReport,
            report: { ...updatedReport.report, must_have: newMustHave },
          };
        }

        return updatedReport;
      }

      return updatedReport;
    });
  };

  const handleGetSuggestions = async () => {
    if (!resumeId || !currentJob?.id) return;

    const res = await getResumesSuggestions(currentJob?.id, resumeId);

    if (res) {
      setSuggestionResume({ ...res });
    }
  };

  const handleReAssess = async () => {
    if (currentTab === "add_skills_experience") {
      const info: { id: string; content: string }[] = [];
      if (report) {
        report.report.must_have?.forEach((item) => {
          info.push({ id: item.id, content: item.content ?? "" });
        });
        report.report.desire_to_have?.forEach((item) => {
          info.push({ id: item.id, content: item.content ?? "" });
        });
      }

      setIsUpdating(true);
      const res: IRevisedResumeReport | null = await updateSkillExperience(
        currentJob.id,
        info,
        resumeId ?? "",
      );
      setIsUpdating(false);

      if (res) {
        setIsEdit(false);
        onReportCallBack?.();
      }
    }

    if (currentTab === "revised_resume") {
      setIsReAssessLoading(true);
      const res = await generateResumeSuggestions(
        currentJob.id,
        resumeId ?? "",
      );
      setIsReAssessLoading(false);

      if (res) {
        setSuggestionResume({ ...res });
      }
    }
  };

  const handleNext = () => {
    if (currentTab === "add_skills_experience") {
      navigate(`/job/${currentJob?.id}?step=4&tab=revised_resume`);
    }

    if (currentTab === "revised_resume") {
      navigate(`/job/${currentJob?.id}?step=5`);
    }
  };

  const handleBack = () => {
    if (currentTab === "add_skills_experience") {
      navigate(`/job/${currentJob?.id}?step=3`);
    }

    if (currentTab === "revised_resume") {
      navigate(`/job/${currentJob?.id}?step=4&tab=add_skills_experience`);
    }
  };

  // effects
  useEffect(() => {
    if (resumeId) {
      handleGetSuggestions();
    }
  }, [resumeId]);

  useEffect(() => {
    console.log(currentReport);
    if (currentReport) {
      let newReport: IRevisedResumeReport = { ...currentReport };
      const skills = currentReport?.resume?.exp_skills ?? [];
      if (skills?.length > 0) {
        newReport = {
          ...currentReport,
          report: {
            ...currentReport.report,
            desire_to_have: currentReport.report?.desire_to_have?.map(
              (item) => ({
                ...item,
                content:
                  skills.find((skill) => skill.id === item.id)?.content ?? "",
              }),
            ),
            must_have: currentReport.report?.must_have?.map((item) => ({
              ...item,
              content:
                skills.find((skill) => skill.id === item.id)?.content ?? "",
            })),
          },
        };
      }
      setReport({ ...newReport });
    }
  }, [currentReport]);

  useEffect(() => {
    if (currentStep !== "4") {
      return;
    }

    if (tab) {
      setCurrentTab(tab as "add_skills_experience" | "revised_resume");
      return;
    }

    navigate(`/job/${currentJob?.id}?step=4&tab=add_skills_experience`);
  }, [tab, currentStep]);

  return (
    <div className="w-full h-full relative pb-14 grow">
      {currentTab === "add_skills_experience" && (
        <>
          {isReAssessLoading ? (
            <div className="w-full h-[40vh] flex items-center justify-center">
              <Loading />
            </div>
          ) : (
            <Card className="pt-6">
              <CardHeader className="hidden">
                <CardTitle>Add skills & experience</CardTitle>
                <CardDescription>
                  Add any skills and experience not yet included in your resume,
                  and then reassess it.
                </CardDescription>
              </CardHeader>
              <CardContent className="">
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader className="bg-gray-300">
                      <TableRow>
                        <TableHead className="font-bold text-black">
                          Requirements
                        </TableHead>
                        <TableHead className="font-bold text-black">
                          Type
                        </TableHead>
                        <TableHead className="font-bold text-black">
                          Match
                        </TableHead>
                        <TableHead className="font-bold text-black w-1/2">
                          Add skills & experience
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report &&
                        report?.report?.desire_to_have &&
                        report?.report?.desire_to_have?.length > 0 &&
                        report?.report?.desire_to_have?.map((desire) => (
                          <TableRow key={`desire-to-have-${desire.id}`}>
                            <TableCell className="font-medium text-nowrap">
                              <p className="text-wrap break-words">
                                {desire.criteria}
                              </p>
                            </TableCell>
                            <TableCell>Desirable</TableCell>
                            <TableCell>{desire.satisfied}</TableCell>
                            <TableCell>
                              {isEdit ? (
                                <EditSkillExperienceInput
                                  disabled={isUpdating}
                                  value={desire?.content ?? ""}
                                  onChange={(value) => {
                                    handleChangeContent(
                                      desire.id,
                                      value,
                                      "desire_to_have",
                                    );
                                  }}
                                />
                              ) : (
                                desire?.content
                              )}
                            </TableCell>
                          </TableRow>
                        ))}

                      {report &&
                        report?.report?.must_have &&
                        report?.report?.must_have?.length > 0 &&
                        report?.report?.must_have?.map((must) => (
                          <TableRow key={`must-have-${must.id}`}>
                            <TableCell className="font-medium text-nowrap">
                              <p className="text-wrap break-words">
                                {must.criteria}
                              </p>
                            </TableCell>
                            <TableCell>Must have</TableCell>
                            <TableCell>{must.satisfied}</TableCell>
                            <TableCell>
                              {isEdit ? (
                                <EditSkillExperienceInput
                                  disabled={isUpdating}
                                  value={must?.content ?? ""}
                                  onChange={(value) => {
                                    handleChangeContent(
                                      must.id,
                                      value,
                                      "must_have",
                                    );
                                  }}
                                />
                              ) : (
                                must?.content
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex mt-6 justify-end gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          disabled={false}
                          className="p-1 h-fit"
                          onClick={() => setIsEdit(!isEdit)}
                        >
                          <FilePenLine className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="dark:text-white text-black text-sm">
                        Edit skills & experience
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {currentTab === "add_skills_experience" && isEdit && (
                    <button
                      className="text-xs w-fit h-fit p-1 border border-black rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      onClick={handleReAssess}
                      disabled={isReAssessLoading || isUpdating}
                    >
                      {isUpdating && (
                        <Loader2 className="size-4 animate-spin" />
                      )}
                      <span>Save changes</span>
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {currentTab === "revised_resume" && (
        <>
          {suggestionResume ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>
                  {suggestionResume?.first_name} {suggestionResume?.last_name}
                </CardTitle>
                <CardDescription>
                  {suggestionResume?.contact_email &&
                    `${suggestionResume?.contact_email} | `}
                  {suggestionResume?.contact_phone &&
                    `${suggestionResume?.contact_phone}`}
                  {suggestionResume?.postal_address &&
                    ` | ${suggestionResume?.postal_address}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="">
                <div className="h-[2px] w-full dark:bg-white bg-black" />
                <div className="space-y-4 mt-6">
                  {suggestionResume?.objective && (
                    <div>
                      <p className="font-semibold uppercase text-sm">
                        objective
                      </p>
                      <div className="h-[1px] w-full dark:bg-white bg-black mt-1" />
                      <p className="text-sm">{suggestionResume.objective}</p>
                    </div>
                  )}

                  {suggestionResume?.skills &&
                    suggestionResume?.skills?.length > 0 && (
                      <div>
                        <p className="font-semibold uppercase text-sm">
                          skills
                        </p>
                        <div className="h-[1px] w-full dark:bg-white bg-black mb-2 mt-1" />
                        <div className="grid grid-cols-2 gap-4">
                          {suggestionResume.skills.map((skill, index) => (
                            <div key={`skill-${index}`}>
                              <p className="text-sm">{skill.name}</p>
                              <ul className="list-decimal list-inside space-y-1 pl-2">
                                {skill?.highlights &&
                                  skill?.highlights?.length > 0 &&
                                  skill?.highlights?.map((hightLight, i) => (
                                    <li
                                      key={`highlight-${i}-${index}`}
                                      className="text-sm"
                                    >
                                      {hightLight}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {suggestionResume?.work_exp &&
                    suggestionResume?.work_exp?.length > 0 && (
                      <div>
                        <p className="font-semibold uppercase text-sm">
                          work experience
                        </p>
                        <div className="h-[1px] w-full dark:bg-white bg-black mb-2 mt-1" />
                        <div className="space-y-2">
                          {suggestionResume.work_exp.map((work, index) => (
                            <div key={`work-${index}`}>
                              <p className="text-sm">
                                {work.company_name} - {work.job_title} (
                                {work.start_month}/{work.start_year} -{" "}
                                {work.end_month}/{work.end_year})
                              </p>
                              <ul className="list-decimal list-inside space-y-1 pl-2">
                                {work?.highlights &&
                                  work?.highlights?.length > 0 &&
                                  work?.highlights?.map((hightLight, i) => (
                                    <li
                                      key={`work-highlight-${i}-${index}`}
                                      className="text-sm"
                                    >
                                      {hightLight}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {suggestionResume?.education &&
                    suggestionResume?.education?.length > 0 && (
                      <div>
                        <p className="font-semibold uppercase text-sm">
                          education
                        </p>
                        <div className="h-[1px] w-full dark:bg-white bg-black mb-2 mt-1" />
                        <div className="space-y-3">
                          {suggestionResume.education.map((edu, index) => (
                            <div key={`edu-${index}`}>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">
                                  {edu.degree}
                                </p>
                                <p className="text-sm">
                                  {edu.start_month}/{edu.start_year} Started
                                </p>
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">
                                  {edu.institution}
                                </p>
                                <p className="text-sm">
                                  {edu.end_month}/{edu.end_year} Earned
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {suggestionResume?.certificates &&
                    suggestionResume?.certificates?.length > 0 && (
                      <div>
                        <p className="font-semibold uppercase text-sm">
                          certifications
                        </p>
                        <div className="h-[1px] w-full dark:bg-white bg-black mb-2 mt-1" />
                        <div className="space-y-3">
                          {suggestionResume.certificates.map(
                            (certificate, index) => (
                              <div key={`edu-${index}`}>
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-semibold">
                                    {certificate.name}
                                  </p>
                                  <p className="text-sm">
                                    {certificate.month}/{certificate.year}
                                  </p>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {suggestionResume?.training &&
                    suggestionResume?.training?.length > 0 && (
                      <div>
                        <p className="font-semibold uppercase text-sm">
                          training/workshops
                        </p>
                        <div className="h-[1px] w-full dark:bg-white bg-black mb-2 mt-1" />
                        <div className="space-y-3">
                          {suggestionResume.education.map((edu, index) => (
                            <div key={`edu-${index}`}>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">
                                  {edu.degree}
                                </p>
                                <p className="text-sm">
                                  {edu.start_month}/{edu.start_year} Started
                                </p>
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">
                                  {edu.institution}
                                </p>
                                <p className="text-sm">
                                  {edu.end_month}/{edu.end_year} Earned
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {suggestionResume?.activity &&
                    suggestionResume?.activity?.length > 0 && (
                      <div>
                        <p className="font-semibold uppercase text-sm">
                          activity and interests
                        </p>
                        <div className="h-[1px] w-full dark:bg-white bg-black mb-2 mt-1" />
                        <ul className="list-decimal list-inside space-y-1">
                          {suggestionResume.activity.map((activity, index) => (
                            <li
                              key={`activity-${index}`}
                              className="text-sm"
                            >
                              {activity}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="w-full h-[40vh] flex items-center justify-center">
              <p className="text-sm">
                "Click "Revise Resume" to see your updated resume
              </p>
            </div>
          )}
        </>
      )}

      <div className="absolute bottom-0 right-0 flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={handleBack}
        >
          Back
        </Button>
        {currentTab === "revised_resume" && (
          <Button
            disabled={isReAssessLoading}
            onClick={handleReAssess}
            className="flex items-center gap-1"
          >
            {isReAssessLoading && <Loader2 className="size-4 animate-spin" />}
            <span className="text-sm">Revise resume</span>
          </Button>
        )}
        <Button
          disabled={isReAssessLoading}
          onClick={handleNext}
        >
          Next Step
        </Button>
      </div>
    </div>
  );
};

export default RevisedResume;
